/**
 * L1: shared iOS chrome every screen composes: the navigation bar with a large title, the
 * account button (top left, opens the wallet switcher as 1Password does), inset groups, cells and
 * the bottom-sheet frame. Screens own their content; the chrome keeps them looking like one app.
 */
import type { AppActions, AppState } from "../contract.js";
import { avatar, h, icon } from "./dom.js";

export function fakeChip(): HTMLElement {
  return h("span", { class: "fake-chip", title: "This build shows fake fixture data, not real secrets" }, "Fake data");
}

/** The avatar button at the top left. Shows the scoped wallet, or "All" when unscoped. */
export function accountButton(state: AppState, actions: AppActions): HTMLElement {
  const wallet = state.snapshot?.wallets.find((w) => w.id === state.scope);
  const name = wallet?.name ?? "All Wallets";
  return h(
    "button",
    { class: "account-btn", "aria-label": `Wallets: ${name}. Open the wallet switcher`, onclick: () => actions.openSheet({ kind: "switcher" }) },
    avatar(name, "avatar"),
    h("span", { class: "account-name" }, name),
    icon("chevron", "icon small"),
  );
}

export interface NavBarOpts {
  left?: Node | null;
  right?: Node | (Node | null)[] | null;
  /** Small centred title (detail screens). */
  title?: string;
  /** iOS large title under the bar (tab roots). */
  largeTitle?: string;
  /** Extra node after the large title (the FAKE DATA chip). */
  largeTitleAccessory?: Node | null;
}

export function navBar(opts: NavBarOpts): HTMLElement {
  const right = Array.isArray(opts.right) ? opts.right : [opts.right ?? null];
  return h(
    "header",
    { class: opts.largeTitle ? "navbar" : "navbar hairline" },
    h(
      "div",
      { class: "navbar-row" },
      h("div", { class: "navbar-side" }, opts.left ?? null),
      h("div", { class: "navbar-title" }, opts.title ?? ""),
      h("div", { class: "navbar-side right" }, ...right),
    ),
    opts.largeTitle ? h("h1", { class: "large-title" }, opts.largeTitle, opts.largeTitleAccessory ?? null) : null,
  );
}

export function backButton(label: string, actions: AppActions): HTMLElement {
  return h("button", { class: "navbar-btn", onclick: () => actions.back() }, icon("back"), label);
}

export function group(title: string | null, cells: (Node | null | false)[], opts: { foot?: string; small?: boolean; action?: Node } = {}): HTMLElement {
  return h(
    "section",
    { class: "group" },
    title ? h("h2", { class: opts.small ? "group-title small" : "group-title" }, h("span", {}, title), opts.action ?? null) : null,
    h("div", { class: "group-body" }, ...cells),
    opts.foot ? h("p", { class: "group-foot" }, opts.foot) : null,
  );
}

export interface CellOpts {
  label: string;
  detail?: string | Node | null;
  lead?: Node | null;
  accessory?: Node | null;
  chevron?: boolean;
  check?: boolean;
  onClick?: () => void;
  /**
   * The cell's tap copies a value. It is marked `data-copy` so the clear-on-next-tap guard leaves
   * the tap alone, and the copy is that tap's only clipboard write.
   */
  copy?: boolean;
  cls?: string;
  disabled?: boolean;
}

export function cell(o: CellOpts): HTMLElement {
  const content = [
    o.lead ?? null,
    h("span", { class: "cell-main" }, o.label),
    o.detail === undefined || o.detail === null ? null : typeof o.detail === "string" ? h("span", { class: "cell-detail" }, o.detail) : o.detail,
    o.check ? icon("check", "icon check") : null,
    o.accessory ?? null,
    o.chevron ? h("span", { class: "cell-accessory" }, icon("chevron", "icon small")) : null,
  ];
  const cls = `cell ${o.cls ?? ""} ${o.lead ? "with-avatar" : ""} ${o.disabled ? "disabled" : ""}`.trim();
  if (o.onClick && !o.disabled) return h("button", { class: cls, "data-copy": o.copy ? "1" : undefined, onclick: o.onClick }, ...content);
  return h("div", { class: cls, "aria-disabled": o.disabled ? "true" : undefined }, ...content);
}

/** Bottom sheet frame. Tapping the backdrop closes it; taps inside do not. */
export function sheetFrame(title: string, actions: AppActions, ...body: (Node | null)[]): HTMLElement {
  const sheet = h(
    "div",
    { class: "sheet", role: "dialog", "aria-modal": "true", "aria-label": title },
    h("div", { class: "sheet-grabber" }),
    h(
      "div",
      { class: "sheet-head" },
      h("span", { class: "sheet-title" }, title),
      h("button", { class: "navbar-btn", onclick: () => actions.closeSheet() }, "Done"),
    ),
    ...body,
  );
  const backdrop = h("div", { class: "sheet-backdrop", onclick: (e: Event) => e.target === backdrop && actions.closeSheet() }, sheet);
  return backdrop;
}

export function banner(kind: "warn" | "error" | "info", title: string, body?: string): HTMLElement {
  const glyph = kind === "info" ? "wallet" : "close";
  return h(
    "div",
    { class: `banner ${kind}`, role: kind === "info" ? "status" : "alert" },
    icon(kind === "warn" ? "archive" : glyph),
    h("div", {}, h("div", { class: "banner-title" }, title), body ? h("div", {}, body) : null),
  );
}

export function emptyState(iconName: string, title: string, body: string): HTMLElement {
  return h("div", { class: "empty-state" }, icon(iconName), h("div", { class: "empty-title" }, title), h("div", {}, body));
}
